/**
 * <ReportIssueDialog> — "something is wrong / I'd like something", in one form.
 *
 * The reporter writes what they saw and what they expected; everything a
 * developer would otherwise have to ask for — which screen, which record, the
 * browser, the errors and failed requests of the last few minutes — is
 * captured AUTOMATICALLY (`errorRing.captureContext` + the host's own
 * `getContext`) and shown collapsed, so they can see exactly what is sent
 * without having to read it.
 *
 * Types and severities come from the application's support POLICY (served in
 * `meta.policy`), never from a list kept here.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Camera, ChevronDown, ChevronRight, ImagePlus, Paperclip, Send, X } from 'lucide-react'

import {
  isQueuedReport, type IssueDetail, type QueuedReport, type SupportMeta, type SupportNotify,
  type SupportRuntime,
} from './contract'
import { captureContext, type CapturedContext } from './errorRing'
import { ATTACHMENT_ACCEPT, attachmentProblem, captureScreenshotFile } from './screenshot'
import {
  ErrorLine, Spinner, SupportModal, btn, errorText, inputStyle, labelStyle, mutedText,
} from './ui'

export interface ReportIssueDialogProps {
  open: boolean
  onClose: () => void
  runtime: SupportRuntime
  /** The meta the host already holds; fetched here when absent. */
  meta?: SupportMeta | null
  /** Host context: the open screen, object, record, app version, surface. */
  getContext?: () => Record<string, unknown>
  /** Where the report comes from: `run` (default) · `builder` · `assistant`. */
  source?: string
  notify?: SupportNotify
  onCreated?: (issue: IssueDetail) => void
  /** A deployed runtime could not reach the support service and kept the
   *  report in its outbox; it is sent automatically later. */
  onQueued?: (report: QueuedReport) => void
  /** Pre-filled values (e.g. an Assistant conversation that became a report). */
  initial?: { title?: string; description?: string; type?: string; severity?: string }
}

export default function ReportIssueDialog({
  open, onClose, runtime, meta: metaProp, getContext, source = 'run', notify, onCreated, onQueued,
  initial,
}: ReportIssueDialogProps) {
  const [meta, setMeta] = useState<SupportMeta | null>(metaProp || null)
  const [metaError, setMetaError] = useState('')
  const [type, setType] = useState('')
  const [severity, setSeverity] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [steps, setSteps] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [context, setContext] = useState<CapturedContext | null>(null)
  const [showContext, setShowContext] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [capturing, setCapturing] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => { if (metaProp) setMeta(metaProp) }, [metaProp])

  // Fetch the vocabularies only when the host did not hand them over.
  useEffect(() => {
    if (!open || meta) return
    let cancelled = false
    runtime.meta()
      .then((m) => { if (!cancelled) { setMeta(m); setMetaError('') } })
      .catch((e) => { if (!cancelled) setMetaError(errorText(e, 'Could not load the issue types.')) })
    return () => { cancelled = true }
  }, [open, meta, runtime])

  // Capture the context the moment the dialog OPENS — that is the state the
  // reporter is describing, not whatever the page does while they type.
  useEffect(() => {
    if (!open) return
    let host: Record<string, unknown> = {}
    try { host = getContext?.() || {} } catch { host = {} }
    setContext(captureContext({ surface: source, ...host }))
    setTitle(initial?.title || '')
    setDescription(initial?.description || '')
    setSteps('')
    setFiles([])
    setError('')
    setShowContext(false)
    // …and a picture of it: the screen behind the dialog (the Support panels
    // are left out of the capture, passwords blanked — see screenshot.ts).
    let cancelled = false
    if (runtime.uploadAttachment) {
      setCapturing(true)
      captureScreenshotFile()
        .then((f) => { if (!cancelled && f) setFiles((cur) => [f, ...cur].slice(0, 10)) })
        .finally(() => { if (!cancelled) setCapturing(false) })
    }
    return () => { cancelled = true }
    // Only on open: re-capturing on every prop change would overwrite the
    // snapshot with a later one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const recapture = useCallback(async () => {
    setCapturing(true)
    try {
      const f = await captureScreenshotFile()
      if (f) setFiles((cur) => [f, ...cur.filter((x) => !x.name.startsWith('screen-'))].slice(0, 10))
      else setError('The screen could not be captured here — attach a screenshot file instead.')
    } finally {
      setCapturing(false)
    }
  }, [])

  const types = useMemo(() => meta?.policy?.issue_types || [], [meta])
  const severities = useMemo(() => meta?.policy?.severities || [], [meta])

  // Defaults: the initial values, else the first type and the MIDDLE severity
  // of the policy's list (a reporter who does not choose has not said "critical").
  useEffect(() => {
    if (!open) return
    const t = initial?.type && types.some((x) => x.id === initial.type) ? initial.type : types[0]?.id || ''
    const s = initial?.severity && severities.some((x) => x.id === initial.severity)
      ? initial.severity
      : severities[Math.floor((severities.length - 1) / 2)]?.id || ''
    setType((cur) => (cur && types.some((x) => x.id === cur) ? cur : t))
    setSeverity((cur) => (cur && severities.some((x) => x.id === cur) ? cur : s))
  }, [open, types, severities, initial?.type, initial?.severity])

  const addFiles = useCallback((list: FileList | null) => {
    if (!list || !list.length) return
    const next: File[] = []
    const problems: string[] = []
    Array.from(list).forEach((f) => {
      const p = attachmentProblem(f)
      if (p) problems.push(p)
      else next.push(f)
    })
    if (next.length) setFiles((cur) => [...cur, ...next].slice(0, 10))
    setError(problems.join('\n'))
  }, [])

  const contextSummary = useMemo(() => {
    if (!context) return ''
    const parts = [
      context.screen && `screen “${context.screen}”`,
      context.obj_name && `object ${context.obj_name}`,
      context.record_id && `record ${String(context.record_id)}`,
      context.browser,
      `${context.client_errors.length} recent error(s)`,
      `${context.failed_calls.length} failed request(s)`,
    ].filter(Boolean)
    return parts.join(' · ')
  }, [context])

  const submit = async () => {
    if (!title.trim()) { setError('Give the issue a short title.'); return }
    setBusy(true)
    setError('')
    let created: IssueDetail | null = null
    try {
      const res = await runtime.createIssue({
        title: title.trim(),
        description: description.trim(),
        type: type || undefined,
        severity: severity || undefined,
        context: { ...(context || {}), ...(steps.trim() ? { steps: steps.trim() } : {}) },
        source,
      })
      if (isQueuedReport(res)) {
        // Saved, not lost — and nothing to attach files to yet (there is no
        // issue number until the report is delivered), so say both.
        setBusy(false)
        notify?.success(res.message || 'The support service is unreachable — your report is saved and will be sent automatically.')
        if (files.length) notify?.error('Attachments are not kept with a saved report; add them once it has been delivered.')
        onQueued?.(res)
        onClose()
        return
      }
      created = res
    } catch (e) {
      setError(errorText(e, 'The issue could not be reported.'))
      setBusy(false)
      return
    }
    // The issue exists now; an attachment that fails is reported, never lost
    // silently — and never makes the reporter file the issue twice.
    const failed: string[] = []
    if (files.length && runtime.uploadAttachment) {
      for (const f of files) {
        try {
          await runtime.uploadAttachment(created.number, f, f.name)
        } catch (e) {
          failed.push(`${f.name}: ${errorText(e, 'upload failed')}`)
        }
      }
    }
    setBusy(false)
    if (failed.length) {
      notify?.error(`Issue #${created.number} was reported, but ${failed.length} attachment(s) failed.`)
    } else {
      notify?.success(`Issue #${created.number} reported — you will be kept posted.`)
    }
    onCreated?.(created)
    if (failed.length) {
      setError(`Issue #${created.number} was reported. These could not be attached:\n${failed.join('\n')}`)
      return
    }
    onClose()
  }

  return (
    <SupportModal open={open} onClose={busy ? () => {} : onClose} title="Report an issue" width={600}>
      <ErrorLine message={metaError} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={labelStyle} htmlFor="twsp-type">Type</label>
          <select id="twsp-type" value={type} onChange={(e) => setType(e.target.value)} style={inputStyle}>
            {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle} htmlFor="twsp-sev">How bad is it?</label>
          <select id="twsp-sev" value={severity} onChange={(e) => setSeverity(e.target.value)} style={inputStyle}>
            {severities.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle} htmlFor="twsp-title">Title</label>
        <input id="twsp-title" value={title} maxLength={300} autoFocus
          onChange={(e) => setTitle(e.target.value)} style={inputStyle}
          placeholder="One line: what is wrong, or what you would like" />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle} htmlFor="twsp-desc">What happened, and what did you expect?</label>
        <textarea id="twsp-desc" value={description} rows={4} maxLength={20000}
          onChange={(e) => setDescription(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle} htmlFor="twsp-steps">Steps to reproduce (optional)</label>
        <textarea id="twsp-steps" value={steps} rows={3} maxLength={5000}
          onChange={(e) => setSteps(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }}
          placeholder={'1. Open …\n2. Click …\n3. See …'} />
      </div>

      {runtime.uploadAttachment && (
        <div style={{ marginBottom: 10 }}>
          <input ref={fileRef} type="file" multiple accept={ATTACHMENT_ACCEPT} style={{ display: 'none' }}
            onChange={(e) => { addFiles(e.target.files); e.target.value = '' }} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" style={btn('subtle', true)} onClick={() => void recapture()}
              disabled={capturing}
              title="Capture the screen behind this dialog again (the Support panels and passwords are left out)">
              <Camera size={13} /> {capturing ? 'Capturing…' : 'Capture the screen'}
            </button>
            <button type="button" style={btn('ghost', true)} onClick={() => fileRef.current?.click()}
              title="Attach a screenshot you took yourself">
              <ImagePlus size={13} /> Attach a screenshot
            </button>
            <button type="button" style={btn('ghost', true)} onClick={() => fileRef.current?.click()}>
              <Paperclip size={13} /> Attach a file
            </button>
            <span style={mutedText}>Images, PDF, text, CSV or video — up to 15 MB each.</span>
          </div>
          {files.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8625rem', marginBottom: 4 }}>
                  <FileThumb file={f} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {f.name} <span style={mutedText}>({Math.ceil(f.size / 1024)} KB)</span>
                  </span>
                  <button type="button" aria-label={`Remove ${f.name}`}
                    onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sp-muted)' }}>
                    <X size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {context && (
        <div style={{
          border: '1px solid var(--sp-border)', borderRadius: 8, padding: '0.45rem 0.6rem',
          marginBottom: 10, background: 'var(--sp-bg2)',
        }}>
          <button type="button" onClick={() => setShowContext((v) => !v)} style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
            cursor: 'pointer', color: 'var(--sp-text)', padding: 0, width: '100%', textAlign: 'left',
          }}>
            {showContext ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <strong style={{ fontSize: '0.8425rem' }}>Sent automatically</strong>
            <span style={{ ...mutedText, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {contextSummary}
            </span>
          </button>
          {showContext && (
            <pre style={{
              margin: '8px 0 0', maxHeight: 220, overflow: 'auto', fontSize: '0.8125rem',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--sp-muted)',
            }}>
              {JSON.stringify(context, null, 2)}
            </pre>
          )}
        </div>
      )}

      <ErrorLine message={error} />

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
        <button type="button" style={btn('ghost')} onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" style={{ ...btn('primary'), opacity: busy ? 0.7 : 1 }}
          onClick={() => { void submit() }} disabled={busy || !title.trim()}>
          {busy ? <Spinner size={14} /> : <Send size={14} />}
          {busy ? 'Reporting…' : 'Report issue'}
        </button>
      </div>
    </SupportModal>
  )
}

/**
 * A small preview of an attachment before it is sent — above all the
 * automatic screen capture, so the reporter SEES what the team will see (and
 * can remove it). Images get a thumbnail that opens full size; anything else
 * a paperclip.
 */
function FileThumb({ file }: { file: File }) {
  const isImage = (file.type || '').startsWith('image/')
  const [url, setUrl] = useState('')
  useEffect(() => {
    if (!isImage) return undefined
    let u = ''
    try { u = URL.createObjectURL(file) } catch { u = '' }
    setUrl(u)
    return () => { if (u) URL.revokeObjectURL(u) }
  }, [file, isImage])
  if (!isImage || !url) return <Paperclip size={12} style={{ flexShrink: 0 }} />
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title="Open full size"
      style={{ flexShrink: 0, display: 'block', lineHeight: 0 }}>
      <img src={url} alt={file.name} style={{
        width: 64, height: 40, objectFit: 'cover', objectPosition: 'top left', borderRadius: 4,
        border: '1px solid var(--sp-border)', background: 'var(--sp-bg2)',
      }} />
    </a>
  )
}
